import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import ArrowIcon from 'assets/images/menu/mobile/mb_arrow_btn.svg';
import { useRecoilValue } from 'recoil';
import { timestampsState } from 'recoils/common/menuState';
import styled, { css } from 'styled-components';

import Button from 'components/button/Button';
import Tag from 'components/common/Tag';

import usePageNavigate from 'hooks/common/usePageNavigate';
import useHeader from 'hooks/menu/useHeader';

import { TokenManager } from 'utils/security/securityUtil';
import { clickToGTM } from 'utils/seo/seoGTMUtil';

import { mobileMenuBarConst } from 'constants/common/menu/mobileMenuBarConst';

import theme from 'styles/token/color';

export const MenuBarMobile = ({
  isToggleMenu,
  setIsToggleMenu,
  isSlideMenu,
  onHandleSubMenuToggle,
}) => {
  const timestamps = useRecoilValue(timestampsState);
  const { location } = usePageNavigate();
  const { onLoginBtnClick } = useHeader();

  useEffect(() => {
    // 모바일 환경의 메뉴 표출 시, 전체 화면 영역 스크롤 동작 제어
    if (isToggleMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'hidden auto';
    }
  }, [isToggleMenu]);

  return (
    <MenuBarWrapMobile className={isToggleMenu ? '' : 'hidden'}>
      <MenuBarInnerMobile className="menubar_inner">
        <MenuBarListMobile className="menu_list">
          {mobileMenuBarConst.map((item, idx) => {
            const isConsole = item.title === 'CONSOLE';
            const isLogin = !!localStorage.getItem(TokenManager.token.access);
            const showSubMenu = item.content && (!isConsole || isLogin);

            return (
              <MenuBarItemMobile
                $isSlideMenu={isSlideMenu[idx]}
                key={idx}
                onClick={() => {
                  // GTM 이벤트 전송
                  clickToGTM({
                    target: item.title,
                    id: `menu-item`,
                    url: window.location.host + item.path,
                  });
                }}
              >
                <Button
                  onClick={() => {
                    if (isConsole && !isLogin) {
                      // 로그인이 안되어있을 경우
                      setIsToggleMenu(false);
                      onLoginBtnClick();
                    } else {
                      onHandleSubMenuToggle(idx);
                    }
                  }}
                >
                  <MenuBadgeTitle className="link_label">
                    <span>{item.title}</span>
                  </MenuBadgeTitle>
                  {showSubMenu && <img src={ArrowIcon} alt="menu toggle" />}
                </Button>
                {/* 서브 메뉴 */}
                {showSubMenu && (
                  <MenuPopperMobile
                    className="sub_menubar"
                    $isSlideMenu={isSlideMenu[idx]}
                  >
                    <SubMenubarListMobile>
                      {item.content &&
                        item.content.map((subMenu, index) => {
                          return (
                            <li
                              className="sub_menu_item"
                              key={index}
                              onClick={() => {
                                // GTM 이벤트 전송
                                clickToGTM({
                                  target: subMenu.title,
                                  id: `sub-menu-item`,
                                  url: window.location.host + subMenu.path,
                                });
                                setIsToggleMenu(false);
                              }}
                            >
                              <Link to={subMenu.path}>
                                <SubLinkLabel
                                  className={
                                    subMenu.path === location.pathname
                                      ? 'highlight'
                                      : ''
                                  }
                                >
                                  <Tag.Flex style={{ gap: '4px' }}>
                                    <span className="title">
                                      {subMenu.title}
                                    </span>
                                    {/* Web과 동일하게 각 항목의 type을 기반으로 타임스탬프 적용 */}
                                    <Tag.Menu
                                      item={{
                                        createdAt:
                                          timestamps?.[subMenu.tagType]
                                            ?.createdAt || subMenu.createdAt,
                                        updatedAt:
                                          timestamps?.[subMenu.tagType]
                                            ?.updatedAt || subMenu.updatedAt,
                                      }}
                                    />
                                  </Tag.Flex>
                                </SubLinkLabel>
                              </Link>
                            </li>
                          );
                        })}
                    </SubMenubarListMobile>
                  </MenuPopperMobile>
                )}
              </MenuBarItemMobile>
            );
          })}
        </MenuBarListMobile>
        {!localStorage.getItem(TokenManager.token.access) && (
          <MenuFooterBox>
            <ul>
              <li
                onClick={() => {
                  setIsToggleMenu(false);
                  onLoginBtnClick();
                }}
              >
                회원가입
              </li>
            </ul>
          </MenuFooterBox>
        )}
      </MenuBarInnerMobile>
    </MenuBarWrapMobile>
  );
};

const MenuBarWrapMobile = styled.div`
  position: absolute;
  top: 60px;
  right: 0;
  width: 0;
  // height: calc(100vh - 66px);
  height: 100vh;
  overflow: hidden;
  background: ${theme.color.white};
  width: 100%;
  transition: transform 300ms;
  &.hidden {
    width: 0;
    transform: translateX(100%);
  }
`;
const MenuBarInnerMobile = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const MenuBarListMobile = styled.ul`
  width: 100%;
`;
const MenuBarItemMobile = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  list-style: none;
  border-bottom: 1px solid ${theme.color.gray200};

  button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 48px;
    padding: 0 16px 0 24px;

    span {
      color: ${theme.color.primaryDark};
      font-size: 15px;
      font-weight: 500;
      position: relative;
    }

    img {
      transition: transform 0.3s ease;
      transform: ${({ $isSlideMenu }) =>
        $isSlideMenu ? 'rotate(0deg)' : 'rotate(-90deg)'};
    }
  }
`;

const MenuPopperMobile = styled.div`
  overflow: hidden;
  transition: max-height 0.3s ease;
  max-height: 0;
  width: 100%;

  ${({ $isSlideMenu }) =>
    $isSlideMenu &&
    css`
      max-height: 300px; /* 서브메뉴의 최대 높이 설정 */
      border-top: 1px solid ${theme.color.gray200};
    `}
`;
const SubMenubarListMobile = styled.ul`
  li {
    font-size: 14px;

    a {
      display: flex;
      align-items: center;
      width: 100%;
      height: 48px;
      padding-left: 38px;
    }

    .title {
      margin-right: 5px;
      color: ${theme.color.primaryDark};

      &.highlight {
        font-weight: 500;
        color: ${theme.color.primary};
      }
    }
  }
`;

const MenuFooterBox = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  padding: 12px 0;
  border-top: 1px solid ${theme.color.gray100};
  background: ${theme.color.white};

  ul {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    // li가 하나뿐일 경우: 가운데 정렬 + 보더 제거
    &:has(> li:only-child) {
      grid-template-columns: 1fr;
      justify-items: center;

      li:last-child {
        border-left: none;
      }
    }

    li {
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${theme.color.gray500};
      font-size: 14px;

      &:last-child {
        border-left: 1px solid ${theme.color.gray200};
      }
    }
  }
`;
const MenuBadgeTitle = styled.div`
  position: relative;
  max-width: max-content;
`;
const SubLinkLabel = styled.div`
  font-size: 14px;
  font-weight: 300;
  color: ${({ theme }) => theme.color.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  .arrow_label {
    display: flex;
  }
`;
